import React from "react";
import { connect } from "react-redux";
import { ToastContainer } from "react-toastify";
import { getTranslate } from "react-localize-redux";
import { Router, Route, Switch } from "react-router-dom";
import { Redirect } from "react-router";

import { setActiveLanguage } from "react-localize-redux";
import { setJobsTableChanged } from "./redux/actions";

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import RefreshSignalR from "./components/signalr/refresh-signalr";

import {
  loadingStop,
  loadingReset,
  resetSmartFlags
} from "./redux/actions";

import Auth from "./auth";
import history from "./history";

/* Components */
import SoftwareVersionBar from "./components/softwareversionbanner";
import ImpersonationBar from "./components/impersonation";
import Navigation from "./components/navbar";
import Header from "./components/header";
import Loading from "./components/loading";
import OrganisationPreferencesPromptSpectrum from "./components/organisation-preferences-prompt-spectrum";

/* Layouts */

/* Pages */
import LoginPage from "./pages/login";
import PasswordSetPage from "./pages/passwordset";
import PasswordResetPage from "./pages/passwordreset";
import ForgotPage from "./pages/forgot";
import SignupPage from "./pages/signup";
import TermsPage from "./pages/terms";
import JobsPage from "./pages/jobs";
import JobPage from "./pages/job";
import ResultsPage from "./pages/results";
import DevicesPage from "./pages/devices";
import DevicePage from "./pages/device";
import AdapterPage from "./pages/adapter";
import CustomersPage from "./pages/customers";
import CustomerPage from "./pages/customer";
import OrganisationsPage from "./pages/organisations";
import OrganisationPage from "./pages/organisation";
import UsersPage from "./pages/users";
import SettingsPage from "./pages/settings";
import SecurityPage from "./pages/security";
import SupportPage from "./pages/support";
import ProfilePage from "./pages/profile";
import UnauthorizedPage from "./pages/unauthorized";
import ReportPage from "./pages/reports";
import JobsRestorePage from "./pages/jobsrestore";
import SubTestSettingsPage from "./pages/subtestsettings";
import SubTestSettingPage from "./pages/subtestsetting";
import DeviceSoftwareReleasesPage from "./pages/devicesoftwarereleases";
import DeviceUpdateRepositoriesPage from "./pages/deviceupdaterepositories";
import HomePage from "./pages/home";
import DashboardPage from "./pages/dashboard";
import SubscriptionPage from "./pages/subscription";
import OrganisationDetailPage from "./pages/organisation-detail";
import TermsAcceptanceLogsPage from "./pages/terms-acceptance-logs";
import SmartFlagsPage from "./pages/smart-flags";
import OrganisationMetricsPage from "./pages/organisation-metrics";
import LinkBudgetCalculatorPage from "./pages/link-budget-calculator";
import JobLogsPage from "./pages/job-logs";
import MetricsPage from "./pages/metrics";
import SubscriptionManagementPage from "./pages/subscription-management";

import withFeatureAccess from "./components/hoc/withFeatureAccess";
import {
  ROLES_ALL,
  ROLES_IDEAL,
  ROLES_IDEAL_ADMIN,
  ROLES_CLIENT,
  ROLES_CLIENT_ADMIN
} from "./common/constants";
import {getAppSetting} from "./redux/actions/appSettingActions";
import LocationTrackingPage from "./pages/location-tracking";
import SmartFlagProfileEditorPage from "./pages/smart-flags/profile-editor";

// HOC-wrapped components must be created at module level to keep stable references.
// Creating them inline in render() causes a new component type on every re-render,
// which makes React unmount/remount → triggering componentDidMount loops.
const ProtectedSmartFlagsPage = withFeatureAccess('smart_flag')(SmartFlagsPage);
const ProtectedOrganisationMetricsPage = withFeatureAccess('organisation_metrics')(OrganisationMetricsPage);
const ProtectedLinkBudgetCalculatorPage = withFeatureAccess('link_budget_calculator')(LinkBudgetCalculatorPage);
const ProtectedLocationTrackingPage = withFeatureAccess('geolocation_device_lock')(LocationTrackingPage);
const ProtectedMetricsPage = withFeatureAccess('organisation_metrics')(MetricsPage);
const ProtectedSmartFlagProfileEditorPage = withFeatureAccess('smart_flag')(SmartFlagProfileEditorPage);

const auth = new Auth();

class Routes extends React.Component {
  constructor(props) {
    super(props);
    this.currentPathname = history.location.pathname;
    this.unlisten = null;
  }

  componentWillMount() {
    this.props.reset();
    const { user } = this.props;

    if (user) {
      this.props.getAppSetting();
    }

    document.title = this.props.translate("header_title");
  }

  componentDidMount() {
    this.unlisten = history.listen(location => {
      const previousPathname = this.currentPathname;
      const nextPathname = location.pathname;

      if (this.shouldResetSmartFlags(previousPathname, nextPathname)) {
        this.props.resetSmartFlags();
      }

      this.currentPathname = nextPathname;
    });
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  getJobContext(pathname) {
    const jobMatch = pathname.match(/^\/job\/([^/]+)$/);
    if (jobMatch) {
      return { jobId: jobMatch[1] };
    }

    const resultsMatch = pathname.match(/^\/results\/([^/]+)\/[^/]+$/);
    if (resultsMatch) {
      return { jobId: resultsMatch[1] };
    }

    return null;
  }

  shouldResetSmartFlags(previousPathname, nextPathname) {
    const previousContext = this.getJobContext(previousPathname);
    const nextContext = this.getJobContext(nextPathname);

    if (!previousContext) {
      return false;
    }

    if (!nextContext) {
      return true;
    }

    return previousContext.jobId !== nextContext.jobId;
  }

  componentDidUpdate() {
    const { loading, resetLoader } = this.props;
    if (loading < 0) resetLoader();
  }

  render() {
    const { translate, loading, profile, monitoringJobsTableChange } = this.props;
    return (
      <DndProvider debugMode={true} backend={HTML5Backend}>
        <div style={{height:"100%"}}>
          <Router history={history}>
            <Switch>
              <Route exact path="/" component={LoginPage} />
              <Route exact path="/login" component={LoginPage} />
              <Route exact path="/forgot" component={ForgotPage} />
              <Route exact path="/signup" component={SignupPage} />
              {/* <Route exact path="/reset/:token/:email" component={ResetPage} /> */}
              <Route exact path="/setpassword" component={PasswordSetPage} />
              <Route exact path="/resetpassword" component={PasswordResetPage} />
              <Route exact path="/terms" component={TermsPage} />
              <Navigation translate={translate}>
                <SoftwareVersionBar translate={translate} />
                <ImpersonationBar translate={translate} />
                <Header translate={translate} />
                <OrganisationPreferencesPromptSpectrum />
                <PrivateRoute
                  exact
                  path="/home"
                  component={HomePage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  exact
                  path="/jobs"
                  component={JobsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  exact
                  path="/jobsview"
                  component={JobsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  exact
                  path="/jobs/:cid"
                  component={JobsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/job/:id"
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                  render={({props, match, location}) => {
                  return <JobPage {...props} match={match} translate={translate} key={location.state && location.state.path !== undefined ? location.state.path : location.pathname} />
                }}
                />
                <PrivateRoute
                  path="/results/:jid/:tid"
                  component={ResultsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/devices"
                  component={DevicesPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_ALL}
                />
                <PrivateRoute
                  path="/device/:deviceid"
                  component={DevicePage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/adapter/:serialno"
                  component={AdapterPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/users"
                  component={UsersPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_ALL}
                />
                <PrivateRoute
                  path="/allusers"
                  component={UsersPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_ALL}
                />
                <PrivateRoute
                  path="/customers"
                  component={CustomersPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/customer/:cid"
                  component={CustomerPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/organisations"
                  component={OrganisationsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL}
                />
                <PrivateRoute
                  path="/organisation/:cid"
                  component={OrganisationPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL}
                />
                <PrivateRoute
                  path="/subscription/:subscriptionId"
                  component={SubscriptionPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/organisation-detail/:organisationId"
                  component={OrganisationDetailPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/terms-acceptance-logs"
                  component={TermsAcceptanceLogsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/support"
                  component={SupportPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL}
                />
                <PrivateRoute
                  path="/settings"
                  component={SettingsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL}
                />
                <PrivateRoute
                  path="/security"
                  component={SecurityPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL}
                />
                <PrivateRoute
                  path="/dashboard"
                  component={DashboardPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/job-logs"
                  component={JobLogsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/subscription-management"
                  component={SubscriptionManagementPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />


                <PrivateRoute
                  path="/profile"
                  component={ProfilePage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_ALL}
                />
                <PrivateRoute
                  path="/reportlist"
                  component={ReportPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_ALL}
                />
                <PrivateRoute
                  path="/restorejobs"
                  component={JobsRestorePage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/subtestsettings"
                  component={SubTestSettingsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT_ADMIN}
                />
                <PrivateRoute
                  exact
                  path="/subtestsetting/:cid"
                  component={SubTestSettingPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT_ADMIN}
                />
                <PrivateRoute
                  path="/devicesoftwarereleases"
                  component={DeviceSoftwareReleasesPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/deviceupdaterepositories"
                  component={DeviceUpdateRepositoriesPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_IDEAL_ADMIN}
                />
                <PrivateRoute
                  path="/smart-flags"
                  component={ProtectedSmartFlagsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/organisation-metrics"
                  component={ProtectedOrganisationMetricsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/link-budget-calculator"
                  component={ProtectedLinkBudgetCalculatorPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/locationTracking"
                  component={ProtectedLocationTrackingPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT_ADMIN}
                />
                <PrivateRoute
                  path="/metrics"
                  component={ProtectedMetricsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  path="/smartflags/:id"
                  component={ProtectedSmartFlagProfileEditorPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
                <PrivateRoute
                  exact
                  path="/smartflags"
                  component={ProtectedSmartFlagsPage}
                  translate={translate}
                  profile={profile}
                  allowed={ROLES_CLIENT}
                />
              </Navigation>
            </Switch>
          </Router>
          {loading > 0 ? <Loading type={"bars"} color={"#EB5281"} /> : null}
          <ToastContainer />
          <RefreshSignalR
          onTableStateChanged={() => {
            if (monitoringJobsTableChange === false) {
              this.props.setJobsTableChanged(true);
            }
          }}
          onSignalRConnected={() => {
          }}
          onSignalRDisconnected={() => {
          }}
          table={"jobs"}
          refreshRate={10000}
        />
        </div>
      </DndProvider>
    );
  }

  // componentWillMount() {
  //   const { user, setLanguage } = this.props;
  //   if (user && user.language) {
  //     //setLanguage(user.language)
  //   }
  // }
}

const PrivateRoute = ({
  component: Component,
  translate: translate,
  allowed: allowed,
  profile: profile,
  ...rest
}) => (
  <Route
    {...rest}
    render={props => {
      if (auth.isAuthenticated()) {
        if (allowed.filter(n => profile.roles.includes(n)).length) {
          return (
            Component ? <Component
              {...props}
              auth={auth}
              translate={translate}
              roles={profile.roles}
            /> : rest.render(props)
          );
        } else {
          return <UnauthorizedPage translate={translate} />;
        }
      } else {
        return (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: props.location }
            }}
          />
        );
      }
    }}
  />
);

const mapDispatchToProps = (dispatch, props) => ({
  reset: () => {
    dispatch(loadingStop());
  },
  setLanguage: language => {
    dispatch(setActiveLanguage(language));
  },
  resetLoader: () => {
    dispatch(loadingReset());
  },
  setJobsTableChanged: (changed) => {
    dispatch(setJobsTableChanged(changed));
  },
  resetSmartFlags: () => {
    dispatch(resetSmartFlags());
  },
  getAppSetting: () => {
    dispatch(getAppSetting());
  },
});

const mapStateToProps = state => ({
  loading: state.loadingReducer.loading,
  translate: getTranslate(state.localeReducer),
  profile: state.profileReducer.profile,
  user: state.userReducer.user,
  monitoringJobsTableChange: state.treeviewReducer.monitoringJobsTableChange,
  ...state
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Routes);
